import { useState, useEffect, useCallback, useRef } from "react";
import toast from "react-hot-toast";
import { FiTrash2, FiCalendar, FiUser, FiFileText, FiDownload } from "react-icons/fi";
import FieldToast from "../components/FieldToast";
import {
  getAllDailyReports,
  getDailyReportById,
  createDailyReport,
  deleteDailyReport,
} from "../api/api";
import {
  extractReportsList,
  normalizeDailyReport,
  matchesDateFilter,
} from "../utils/normalizeDailyReport";
import "./ReportDaily.css";

const PAGE_SIZE = 10;
const FETCH_LIMIT = 100;

function extractReportItem(payload) {
  if (!payload) return null;
  if (payload._id || payload.id) return normalizeDailyReport(payload);
  const nested = payload.data ?? payload.report ?? payload.dailyReport ?? null;
  return nested ? normalizeDailyReport(nested) : null;
}

// ── Convert SVG Base64 → PNG Base64 via canvas ──────────────────────────────
function svgBase64ToPngBase64(svgBase64, width = 300, height = 120) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width  = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = reject;
    img.src = svgBase64;
  });
}

// ✅ Download report as PDF using jsPDF
async function downloadReportAsPDF(report) {
  if (!window.jspdf) {
    await new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

  const pageW    = doc.internal.pageSize.getWidth();
  const pageH    = doc.internal.pageSize.getHeight();
  const margin   = 20;
  const contentW = pageW - margin * 2;

  // ── Colours ────────────────────────────────────────────────
  const darkGreen  = [22,  39,  39];   // #162727
  const lightGreen = [150, 205, 176];  // #96cdb0
  const gold       = [192, 141,  81];  // #c08d51
  const white      = [255, 255, 255];
  const black      = [20,  20,  20];
  const grey       = [100, 100, 100];

  // ══════════════════════════════════════════════════════════
  // HEADER BAND
  // ══════════════════════════════════════════════════════════
  doc.setFillColor(...darkGreen);
  doc.rect(0, 0, pageW, 22, "F");

  // Thin gold accent line under header
  doc.setFillColor(...gold);
  doc.rect(0, 22, pageW, 1.2, "F");

  // ── Logo: SVG Base64 → PNG via canvas ─────────────────────
  const LOGO_SVG_BASE64 = "data:image/svg+xml;base64,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";

  try {
    const logoPng = await svgBase64ToPngBase64(LOGO_SVG_BASE64, 600, 300);
    doc.addImage(logoPng, "PNG", margin, 3, 28, 14);
  } catch (e) {
    // Fallback: text logo if canvas conversion fails
    doc.setTextColor(255, 255, 255);
    doc.setFont("times", "bold");
    doc.setFontSize(13);
    doc.text("Growth Station", margin, 13);
  }

  // "Daily Report" label flush right in header
  doc.setTextColor(...lightGreen);
  doc.setFont("times", "italic");
  doc.setFontSize(10);
  doc.text("Daily Report", pageW - margin, 13, { align: "right" });

  let y = 34;

  // ══════════════════════════════════════════════════════════
  // TITLE ROW
  // ══════════════════════════════════════════════════════════
  doc.setTextColor(...darkGreen);
  doc.setFont("times", "bold");
  doc.setFontSize(22);
  doc.text("Daily Report", margin, y);
  y += 9;

  // ── Meta info ──────────────────────────────────────────────
  doc.setFontSize(10);
  doc.setFont("times", "normal");
  doc.setTextColor(...grey);

  const date    = report.createdAt ? new Date(report.createdAt).toLocaleString() : "—";
  const author  = report.preparedBy?.userName || "Unknown";
  const project = report.projectName || "—";

  doc.text(`Submitted by: ${author}`, margin, y); y += 5;
  doc.text(`Date: ${date}`,           margin, y); y += 5;
  doc.text(`Project: ${project}`,     margin, y); y += 9;

  // ── Divider ────────────────────────────────────────────────
  doc.setFillColor(...lightGreen);
  doc.rect(margin, y, contentW, 0.8, "F");
  y += 6;

  // ══════════════════════════════════════════════════════════
  // SECTION HELPER
  // ══════════════════════════════════════════════════════════
  const section = (label, value) => {
    if (!value) return;

    if (y > pageH - 30) { doc.addPage(); y = 20; }

    // Gold label
    doc.setFontSize(11);
    doc.setFont("times", "bold");
    doc.setTextColor(...gold);
    doc.text(label, margin, y);
    y += 1.5;

    // Thin dark-green underline under label
    doc.setFillColor(...darkGreen);
    doc.rect(margin, y, doc.getTextWidth(label), 0.4, "F");
    y += 5;

    // Black body text (wrapped)
    doc.setFontSize(10.5);
    doc.setFont("times", "normal");
    doc.setTextColor(...black);
    const lines = doc.splitTextToSize(value, contentW);
    lines.forEach((line) => {
      if (y > pageH - 20) { doc.addPage(); y = 20; }
      doc.text(line, margin, y);
      y += 5.5;
    });
    y += 5;
  };

  section("Summary",           report.summary);
  section("Tasks Completed",   report.tasksCompleted);
  section("Tasks In Progress", report.tasksInProgress);
  section("Next Steps",        report.nextSteps);
  if (report.notes) section("Notes", report.notes);

  // ══════════════════════════════════════════════════════════
  // FOOTER — every page
  // ══════════════════════════════════════════════════════════
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);

    // Gold footer bar
    doc.setFillColor(...gold);
    doc.rect(0, pageH - 10, pageW, 10, "F");

    // Page number center
    doc.setFontSize(8);
    doc.setFont("times", "normal");
    doc.setTextColor(...white);
    doc.text(
      `Page ${i} of ${pageCount}`,
      pageW / 2,
      pageH - 3.5,
      { align: "center" }
    );

    // Light green left section in footer with company name
    doc.setFillColor(...lightGreen);
    doc.rect(0, pageH - 10, 50, 10, "F");
    doc.setTextColor(...darkGreen);
    doc.setFont("times", "bold");
    doc.setFontSize(8);
    doc.text("Growth Station", 4, pageH - 3.5);
  }

  // ── Save ───────────────────────────────────────────────────
  const fileName = `daily-report-${report.projectName || "report"}-${
    report.createdAt
      ? new Date(report.createdAt).toLocaleDateString("en-GB").replace(/\//g, "-")
      : "date"
  }.pdf`;

  doc.save(fileName);
}

export default function ReportDaily() {
  const [view, setView] = useState("form");
  const [selectedReport, setSelectedReport] = useState(null);
  const [filteredReports, setFilteredReports] = useState([]);
  const [dateFilter, setDateFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalMatching, setTotalMatching] = useState(0);

  const [formData, setFormData] = useState({
    projectName: "",
    summary: "",
    tasksCompleted: "",
    tasksInProgress: "",
    nextSteps: "",
    notes: "",
  });

  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const projectNameRef     = useRef(null);
  const summaryRef         = useRef(null);
  const tasksCompletedRef  = useRef(null);
  const tasksInProgressRef = useRef(null);

  const [fieldToast, setFieldToast] = useState({
    show: false,
    message: "",
    targetRef: null,
  });

  const showFieldToast = (message, targetRef) => {
    setFieldToast({ show: true, message, targetRef });
    setTimeout(() => {
      setFieldToast((prev) => ({ ...prev, show: false }));
    }, 3000);
  };

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAllDailyReports(1, FETCH_LIMIT);
      const allReports = extractReportsList(res)
        .map(normalizeDailyReport)
        .filter(Boolean);

      const matched = allReports
        .filter((report) => matchesDateFilter(report.createdAt, dateFilter))
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      const pages    = Math.max(1, Math.ceil(matched.length / PAGE_SIZE));
      const safePage = Math.min(currentPage, pages);
      const start    = (safePage - 1) * PAGE_SIZE;

      setTotalMatching(matched.length);
      setTotalPages(pages);
      setFilteredReports(matched.slice(start, start + PAGE_SIZE));

      if (safePage !== currentPage) setCurrentPage(safePage);
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || "Error fetching reports";
      toast.error(message);
      setFilteredReports([]);
      setTotalMatching(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, dateFilter]);

  const fetchReportById = async (id) => {
    setLoading(true);
    try {
      const res    = await getDailyReportById(id);
      const report = extractReportItem(res);
      if (!report) throw new Error("Report not found");
      setSelectedReport(report);
      setView("detail");
    } catch (error) {
      toast.error("Error fetching report details");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.projectName?.trim()) {
      showFieldToast("Project name is required", projectNameRef); return;
    }
    if (!formData.summary?.trim()) {
      showFieldToast("Summary is required", summaryRef); return;
    }
    if (!formData.tasksCompleted?.trim()) {
      showFieldToast("Tasks completed is required", tasksCompletedRef); return;
    }
    if (!formData.tasksInProgress?.trim()) {
      showFieldToast("Tasks in progress is required", tasksInProgressRef); return;
    }
    if (formData.projectName.trim().length < 3) {
      showFieldToast("Project name must be at least 3 characters", projectNameRef); return;
    }
    if (formData.summary.trim().length < 10) {
      showFieldToast("Summary must be at least 10 characters", summaryRef); return;
    }
    if (formData.tasksCompleted.trim().length < 5) {
      showFieldToast("Tasks completed must be at least 5 characters", tasksCompletedRef); return;
    }
    if (formData.tasksInProgress.trim().length < 5) {
      showFieldToast("Tasks in progress must be at least 5 characters", tasksInProgressRef); return;
    }

    setLoading(true);
    try {
      await createDailyReport(formData);
      toast.success("Report submitted successfully!");
      setFormData({ projectName: "", summary: "", tasksCompleted: "", tasksInProgress: "", nextSteps: "", notes: "" });
    } catch (error) {
      toast.error("Error submitting report");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this report?")) return;
    setLoading(true);
    try {
      await deleteDailyReport(id);
      toast.success("Report deleted successfully!");
      if (selectedReport?._id === id) { setSelectedReport(null); setView("table"); }
      await fetchReports();
    } catch (error) {
      toast.error("Error deleting report");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (report) => {
    const id = report._id ?? report.id;
    setDownloadingId(id);
    try {
      let fullReport = report;
      if (!report.summary || report.summary.endsWith("...")) {
        const res = await getDailyReportById(id);
        fullReport = extractReportItem(res) || report;
      }
      await downloadReportAsPDF(fullReport);
      toast.success("PDF downloaded!");
    } catch (err) {
      toast.error("Failed to download PDF");
      console.error(err);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDateFilter  = (e) => { setDateFilter(e.target.value); setCurrentPage(1); };
  const handlePageChange  = (page) => setCurrentPage(page);
  const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  useEffect(() => {
    if (view === "table" || view === "detail") fetchReports();
  }, [view, fetchReports]);

  const emptyMessage = dateFilter
    ? `No reports found for ${dateFilter}. Try clearing the date filter.`
    : "No reports found";

  return (
    <div className="report-daily-container">
      <div className="report-daily-header">
        <h1>Daily Report</h1>
        <div className="view-toggle">
          <button className={view === "form" ? "active" : ""} onClick={() => setView("form")}>
            <FiFileText /> Submit Report
          </button>
          <button
            className={view === "table" || view === "detail" ? "active" : ""}
            onClick={() => { setSelectedReport(null); setCurrentPage(1); setView("table"); }}
          >
            <FiCalendar /> View Reports
          </button>
        </div>
      </div>

      {view === "form" && (
        <div className="report-form-container">
          <form onSubmit={handleSubmit} className="report-form">
            <div className="form-group">
              <label>Project Name</label>
              <input ref={projectNameRef} type="text" name="projectName" value={formData.projectName} onChange={handleInputChange} required placeholder="Enter project name" />
            </div>
            <div className="form-group">
              <label>Summary</label>
              <textarea ref={summaryRef} name="summary" value={formData.summary} onChange={handleInputChange} required placeholder="Brief summary of today's work" rows="3" />
            </div>
            <div className="form-group">
              <label>Tasks Completed</label>
              <textarea ref={tasksCompletedRef} name="tasksCompleted" value={formData.tasksCompleted} onChange={handleInputChange} required placeholder="List completed tasks" rows="3" />
            </div>
            <div className="form-group">
              <label>Tasks In Progress</label>
              <textarea ref={tasksInProgressRef} name="tasksInProgress" value={formData.tasksInProgress} onChange={handleInputChange} required placeholder="List tasks currently in progress" rows="3" />
            </div>
            <div className="form-group">
              <label>Next Steps (Optional)</label>
              <textarea name="nextSteps" value={formData.nextSteps} onChange={handleInputChange} placeholder="Planned next steps" rows="3" />
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea name="notes" value={formData.notes} onChange={handleInputChange} placeholder="Additional notes" rows="2" />
            </div>
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? "Submitting..." : "Submit Report"}
            </button>
          </form>
        </div>
      )}

      {(view === "table" || view === "detail") && (
        <div className="reports-table-container">
          <div className="filter-section">
            <div className="date-filter">
              <label><FiCalendar /> Filter by Date:</label>
              <input type="date" value={dateFilter} onChange={handleDateFilter} />
              {dateFilter && (
                <button className="clear-filter-btn" onClick={() => { setDateFilter(""); setCurrentPage(1); }}>
                  Clear
                </button>
              )}
            </div>
            {dateFilter && (
              <p className="filter-hint">
                Showing reports for {dateFilter}
                {totalMatching > 0 ? ` (${totalMatching} found)` : ""}
              </p>
            )}
          </div>

          {view === "table" && (
            <>
              {loading ? (
                <div className="loading">Loading reports...</div>
              ) : (
                <>
                  <div className="reports-table-wrapper">
                    <table className="reports-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Submitted By</th>
                          <th>Project</th>
                          <th>Summary</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {!Array.isArray(filteredReports) || filteredReports.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="no-data">{emptyMessage}</td>
                          </tr>
                        ) : (
                          filteredReports.map((report) => {
                            const id = report._id ?? report.id;
                            return (
                              <tr key={id}>
                                <td>{new Date(report.createdAt).toLocaleDateString()}</td>
                                <td>
                                  <button className="user-link" onClick={() => fetchReportById(id)}>
                                    <FiUser /> {report.preparedBy?.userName || "Unknown"}
                                  </button>
                                </td>
                                <td>{report.projectName}</td>
                                <td className="summary-cell">
                                  {report.summary?.substring(0, 50)}
                                  {(report.summary?.length ?? 0) > 50 ? "..." : ""}
                                </td>
                                <td style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                  <button
                                    className="delete-btn"
                                    title="Download PDF"
                                    disabled={downloadingId === id}
                                    onClick={() => handleDownload(report)}
                                    style={{ color: "#c08d51" }}
                                  >
                                    {downloadingId === id ? "..." : <FiDownload />}
                                  </button>
                                  <button
                                    className="delete-btn"
                                    onClick={() => handleDelete(id)}
                                    title="Delete report"
                                  >
                                    <FiTrash2 />
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  {totalPages > 1 && (
                    <div className="pagination">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          className={currentPage === page ? "active" : ""}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {view === "detail" && selectedReport && (
            <div className="report-detail">
              <button className="back-btn" onClick={() => { setView("table"); setSelectedReport(null); }}>
                ← Back to Reports
              </button>

              <div className="detail-header">
                <h2>Report Details</h2>
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    className="delete-btn-large"
                    onClick={() => handleDownload(selectedReport)}
                    disabled={downloadingId === (selectedReport._id ?? selectedReport.id)}
                    style={{ background: "#162727", borderColor: "#c08d51", color: "#fff" }}
                  >
                    <FiDownload />{" "}
                    {downloadingId === (selectedReport._id ?? selectedReport.id) ? "Generating..." : "Download PDF"}
                  </button>
                  <button className="delete-btn-large" onClick={() => handleDelete(selectedReport._id ?? selectedReport.id)}>
                    <FiTrash2 /> Delete Report
                  </button>
                </div>
              </div>

              <div className="detail-content">
                <div className="detail-row"><span className="label">Submitted By:</span><span className="value">{selectedReport.preparedBy?.userName || "Unknown"}</span></div>
                <div className="detail-row"><span className="label">Date:</span><span className="value">{new Date(selectedReport.createdAt).toLocaleString()}</span></div>
                <div className="detail-row"><span className="label">Project Name:</span><span className="value">{selectedReport.projectName}</span></div>
                <div className="detail-row"><span className="label">Summary:</span><span className="value">{selectedReport.summary}</span></div>
                <div className="detail-row"><span className="label">Tasks Completed:</span><span className="value">{selectedReport.tasksCompleted}</span></div>
                <div className="detail-row"><span className="label">Tasks In Progress:</span><span className="value">{selectedReport.tasksInProgress}</span></div>
                <div className="detail-row"><span className="label">Next Steps:</span><span className="value">{selectedReport.nextSteps}</span></div>
                <div className="detail-row"><span className="label">Notes:</span><span className="value">{selectedReport.notes || "N/A"}</span></div>
              </div>
            </div>
          )}
        </div>
      )}

      <FieldToast message={fieldToast.message} targetRef={fieldToast.targetRef} show={fieldToast.show} />
    </div>
  );
}